import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronRight,
  Check,
  Rocket,
  Smartphone,
  Building2,
  MapPin,
  Phone,
  ArrowRight,
  Monitor,
  Copy,
  Share2,
} from 'lucide-react';
import { useLoadScript } from '@react-google-maps/api';
import {
  Form,
  FormField,
  Input,
  applyServerErrors,
  useZodForm,
} from '@relay-towing/ui';
import CreateDriverScreen from '@/screens/drivers/CreateDriverScreen';
import InviteDispatchersOnboardingScreen from '@/screens/onboarding/InviteDispatchersOnboardingScreen';
import { CustomAutocomplete } from '@/components/ui/CustomAutocomplete';
import env from '@/config/env';
import {
  businessProfileSchema,
  type BusinessProfileValues,
} from '@/lib/businessProfileSchema';
import { ONBOARDING_COMPLETED_KEY } from '@/lib/onboardingGate';
import { useCompanyInfo, useUpdateCompany } from '@/api/hooks/useCompanyInfo';
import type { UpdateCompanyPayload } from '@/api/hooks/useCompanyInfo';
import {
  formatCompanyAddress,
  snapshotCompanyFields,
  parseAddressComponents,
} from '@/lib/companyAddress';
import { useToast } from '@/contexts/ToastContext';
import OnboardingStepChrome from './OnboardingStepChrome';
import { STEPS, type StepId } from './onboardingSteps';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_LIBS: 'places'[] = ['places'];

type View = 'checklist' | StepId;

const pageVariants = {
  enter: (direction: number) => ({
    transform: `translateX(${direction > 0 ? '20px' : '-20px'})`,
    opacity: 0,
  }),
  center: {
    transform: 'translateX(0px)',
    opacity: 1,
  },
  exit: (direction: number) => ({
    transform: `translateX(${direction > 0 ? '-20px' : '20px'})`,
    opacity: 0,
  }),
};

const pageTransition = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1],
};

const ReviewProfileStep: React.FC<{
  onComplete: () => void;
  onBack: () => void;
}> = ({ onComplete, onBack }) => {
  const { data, isLoading, error: fetchError } = useCompanyInfo();
  const updateCompany = useUpdateCompany();
  const { isLoaded: isMapsLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBS,
  });

  const form = useZodForm(businessProfileSchema, {
    defaultValues: { name: '', companyPhone: '', addressLine: '' },
  });
  /* What Places gave us for the typed address, if anything. Not a field:
     it is derived from the pick, and typing over it clears it. */
  const [structuredAddress, setStructuredAddress] = useState<{
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
  } | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data && !initialized) {
      const snap = snapshotCompanyFields(data);
      form.reset({
        name: snap.name,
        companyPhone: snap.companyPhone,
        addressLine: formatCompanyAddress(
          snap.streetAddress,
          snap.city,
          snap.state,
          snap.zipCode
        ),
      });
      setStructuredAddress({
        streetAddress: snap.streetAddress,
        city: snap.city,
        state: snap.state,
        zipCode: snap.zipCode,
      });
      setInitialized(true);
    }
  }, [data, initialized, form]);

  const handleSubmit = async (values: BusinessProfileValues) => {
    try {
      const payload: UpdateCompanyPayload = {
        name: values.name,
        companyPhone: values.companyPhone,
        ...(structuredAddress ?? { streetAddress: values.addressLine.trim() }),
      };
      await updateCompany.mutateAsync(payload);
      onComplete();
    } catch (err: any) {
      applyServerErrors(
        form,
        err,
        err?.message || 'Failed to save. Please try again.'
      );
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <OnboardingStepChrome
        filledSegmentCount={1}
        segmentCount={STEPS.length}
        title="Review Profile"
        subtitle="Business name and location"
        onBack={onBack}
      />

      {/* Body */}
      <Form
        form={form}
        gap={0}
        onSubmit={handleSubmit}
        className="flex flex-1 flex-col"
      >
        <div className="flex-1 px-6 py-6 space-y-5">
          <p className="text-sm" style={{ color: '#6B7280' }}>
            Enter your towing company details to get started with Relay Tow
            dispatch.
          </p>

          {fetchError && (
            <div
              className="rounded-xl p-4 border text-sm"
              style={{
                backgroundColor: '#FEF2F2',
                borderColor: '#FECACA',
                color: '#991B1B',
              }}
            >
              {fetchError.message || 'Failed to load company info.'}
            </div>
          )}

          {/* Business Name */}
          <FormField form={form} name="name" label="Business Name" size="sm">
            {(field) => (
              <Input
                {...field}
                size="sm"
                leadingIcon={<Building2 size={18} />}
                placeholder="Your company name"
                disabled={isLoading}
              />
            )}
          </FormField>

          {/* Business Address */}
          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: '#374151' }}
            >
              Business Address
            </label>
            <div
              className="relative rounded-xl border"
              style={{ borderColor: '#E5E7EB' }}
            >
              <MapPin
                size={18}
                style={{
                  color: '#9CA3AF',
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
              {isMapsLoaded ? (
                <CustomAutocomplete
                  value={form.watch('addressLine')}
                  onChange={(value) => {
                    form.setValue('addressLine', value);
                    setStructuredAddress(null);
                  }}
                  onPlaceSelected={(place) => {
                    const parsed = parseAddressComponents(
                      place.address_components ?? []
                    );
                    form.setValue('addressLine', place.formatted_address);
                    setStructuredAddress({
                      streetAddress: parsed.streetAddress,
                      city: parsed.city,
                      state: parsed.state,
                      zipCode: parsed.zipCode,
                    });
                  }}
                  placeholder="Street, City, State, ZIP"
                  className="w-full text-sm outline-none rounded-xl"
                  style={{
                    paddingLeft: '42px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={form.watch('addressLine')}
                  onChange={(e) => {
                    form.setValue('addressLine', e.target.value);
                    setStructuredAddress(null);
                  }}
                  placeholder="Street, City, State, ZIP"
                  disabled={isLoading}
                  className="w-full text-sm outline-none bg-transparent rounded-xl"
                  style={{
                    paddingLeft: '42px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    color: '#111827',
                  }}
                />
              )}
            </div>
          </div>

          {/* Phone Number */}
          <FormField
            form={form}
            name="companyPhone"
            label="Phone Number"
            size="sm"
          >
            {(field) => (
              <Input
                {...field}
                size="sm"
                type="tel"
                leadingIcon={<Phone size={18} />}
                placeholder="+1 (555) 000-0000"
                disabled={isLoading}
              />
            )}
          </FormField>
        </div>

        {/* Footer */}
        <div
          className="border-t px-6 pb-10 pt-4"
          style={{ borderColor: '#E5E7EB' }}
        >
          <button
            type="submit"
            disabled={isLoading || updateCompany.isPending}
            className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity"
            style={{
              backgroundColor: '#172F27',
              opacity: isLoading || updateCompany.isPending ? 0.5 : 1,
            }}
          >
            {updateCompany.isPending ? 'Saving…' : 'Save & Continue'}
            {!updateCompany.isPending && <ArrowRight size={18} />}
          </button>
        </div>
      </Form>
    </div>
  );
};

const DRIVER_APP_STEP = STEPS.find((s) => s.id === 'driverApp')!;

const linkRowCardClass =
  'flex flex-1 min-w-0 items-center gap-4 p-4 rounded-xl border';

const AppLinkRow: React.FC<{
  url: string;
  shareTitle: string;
  children: React.ReactNode;
}> = ({ url, shareTitle, children }) => {
  const { showToast } = useToast();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('success', 'Link copied');
    } catch {
      showToast('error', 'Could not copy link');
    }
  };

  const shareLink = async () => {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function'
    ) {
      try {
        await navigator.share({ url, title: shareTitle });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }
    await copyLink();
  };

  const iconBtnClass =
    'p-2.5 rounded-xl border flex-shrink-0 flex items-center justify-center border-gray-200 bg-background text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-900 active:bg-gray-200';

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button"
          onClick={copyLink}
          className={iconBtnClass}
          aria-label="Copy link"
        >
          <Copy size={18} />
        </button>
        <button
          type="button"
          onClick={shareLink}
          className={iconBtnClass}
          aria-label="Share link"
        >
          <Share2 size={18} />
        </button>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkRowCardClass}
        style={{ borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' }}
      >
        {children}
      </a>
    </div>
  );
};

const DriverAppStep: React.FC<{
  onComplete: () => void;
  onBack: () => void;
}> = ({ onComplete, onBack }) => (
  <div className="flex flex-col min-h-screen bg-background">
    <OnboardingStepChrome
      filledSegmentCount={4}
      segmentCount={STEPS.length}
      title="Team Member Apps"
      subtitle={DRIVER_APP_STEP.subtitle}
      onBack={onBack}
    />

    <div className="flex-1 px-6 py-6 space-y-8 overflow-y-auto">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold" style={{ color: '#111827' }}>
          Driver Mobile Apps
        </h3>
        <p className="text-sm" style={{ color: '#6B7280' }}>
          Share these links with your drivers so they can download the Relay Tow
          app on their phones.
        </p>
        <AppLinkRow url={env.appStore.ios} shareTitle="Relay Driver – iOS">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#172F27' }}
          >
            <Smartphone size={20} color="white" />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: '#111827' }}>
              iOS – App Store
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
              Download on the Apple App Store
            </p>
          </div>
          <ChevronRight
            size={16}
            style={{ color: '#9CA3AF', marginLeft: 'auto' }}
          />
        </AppLinkRow>
        <AppLinkRow
          url={env.appStore.android}
          shareTitle="Relay Driver – Android"
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#172F27' }}
          >
            <Smartphone size={20} color="white" />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: '#111827' }}>
              Android – Google Play
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
              Get it on Google Play
            </p>
          </div>
          <ChevronRight
            size={16}
            style={{ color: '#9CA3AF', marginLeft: 'auto' }}
          />
        </AppLinkRow>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold" style={{ color: '#111827' }}>
          Dispatch Web App
        </h3>
        <p className="text-sm" style={{ color: '#6B7280' }}>
          Share this link with your dispatchers to access the online dashboard.
        </p>
        <AppLinkRow url={env.dashboardUrl} shareTitle="Relay Tow dispatch">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#172F27' }}
          >
            <Monitor size={20} color="white" />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: '#111827' }}>
              Desktop - Web Browser
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
              Complete dispatch management
            </p>
          </div>
          <ChevronRight
            size={16}
            style={{ color: '#9CA3AF', marginLeft: 'auto' }}
          />
        </AppLinkRow>
      </div>
    </div>

    <div
      className="border-t px-6 pb-10 pt-4 mt-auto"
      style={{ borderColor: '#E5E7EB' }}
    >
      <button
        type="button"
        onClick={onComplete}
        className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
        style={{ backgroundColor: '#172F27' }}
      >
        Got it, continue
        <ArrowRight size={18} />
      </button>
    </div>
  </div>
);

const OnboardingScreen: React.FC = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<View>('checklist');
  const [direction, setDirection] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<StepId>>(new Set());

  const completedCount = completedSteps.size;
  const progressPercent = (completedCount / STEPS.length) * 100;
  const allComplete = completedCount === STEPS.length;

  const goToStep = (stepId: StepId) => {
    setDirection(1);
    setCurrentView(stepId);
  };

  const goToChecklist = () => {
    setDirection(-1);
    setCurrentView('checklist');
  };

  const markComplete = (stepId: StepId) => {
    setCompletedSteps((prev) => new Set([...prev, stepId]));
    goToChecklist();
  };

  const finish = () => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    navigate('/', { replace: true });
  };

  const renderStepView = () => {
    switch (currentView) {
      case 'profile':
        return (
          <ReviewProfileStep
            onBack={goToChecklist}
            onComplete={() => markComplete('profile')}
          />
        );
      case 'drivers':
        return (
          <CreateDriverScreen
            variant="onboarding"
            showHeader={true}
            showTruckSection={false}
            onBack={goToChecklist}
            onSuccess={() => markComplete('drivers')}
            onSkip={() => markComplete('drivers')}
          />
        );
      case 'dispatchers':
        return (
          <InviteDispatchersOnboardingScreen
            onBack={goToChecklist}
            onSuccess={() => markComplete('dispatchers')}
            onSkip={() => markComplete('dispatchers')}
          />
        );
      case 'driverApp':
        return (
          <DriverAppStep
            onBack={goToChecklist}
            onComplete={() => markComplete('driverApp')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden relative">
      <AnimatePresence mode="wait" custom={direction}>
        {currentView === 'checklist' ? (
          <motion.div
            key="checklist"
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
            className="min-h-screen flex flex-col"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            {/* Progress bar */}
            <div className="h-1 bg-gray-100">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: '#172F27',
                }}
              />
            </div>

            <div className="flex-1 px-6 pt-10 pb-6">
              {/* Header */}
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: '#172F27' }}
              >
                <Rocket size={22} color="white" />
              </div>

              <h1
                className="text-2xl font-bold mb-1"
                style={{ color: '#111827' }}
              >
                Let's set up your business
              </h1>
              <p className="text-sm mb-8" style={{ color: '#6B7280' }}>
                Complete these steps to start dispatching with Relay Tow.
              </p>

              {/* Step list */}
              <div className="space-y-3">
                {STEPS.map((step) => {
                  const done = completedSteps.has(step.id);
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => goToStep(step.id)}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-colors"
                      style={{
                        backgroundColor: done ? '#F9FAFB' : '#FFFFFF',
                        borderColor: done ? '#D1FAE5' : '#E5E7EB',
                      }}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold transition-colors"
                        style={{
                          backgroundColor: done ? '#172F27' : '#F3F4F6',
                          color: done ? '#FFFFFF' : '#6B7280',
                        }}
                      >
                        {done ? <Check size={14} /> : step.number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="font-semibold text-sm"
                          style={{ color: done ? '#6B7280' : '#111827' }}
                        >
                          {step.title}
                        </p>
                        <p
                          className="text-xs mt-0.5"
                          style={{ color: '#9CA3AF' }}
                        >
                          {step.subtitle}
                        </p>
                      </div>
                      <ChevronRight
                        size={18}
                        style={{ color: '#D1D5DB', flexShrink: 0 }}
                      />
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={finish}
                className="mt-3 w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity"
                style={{ backgroundColor: '#172F27' }}
              >
                Finish Set up
                <ArrowRight size={18} />
              </button>

              {/* CTA */}
              {allComplete && (
                <button
                  type="button"
                  onClick={finish}
                  className="mt-6 w-full py-3 rounded-xl font-semibold text-white"
                  style={{ backgroundColor: '#172F27' }}
                >
                  Go to dashboard
                </button>
              )}
            </div>

            {/* Footer */}
            <div
              className="px-6 pb-10 flex items-center justify-between border-t"
              style={{ borderColor: '#F3F4F6' }}
            >
              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                Estimated setup time:{' '}
                <span className="font-semibold" style={{ color: '#374151' }}>
                  Under 5 minutes
                </span>
              </p>
              <button
                type="button"
                onClick={finish}
                className="text-xs font-medium"
                style={{ color: '#9CA3AF' }}
              >
                Skip for now
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={currentView}
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
            className="min-h-screen"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            {renderStepView()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingScreen;
